import React from 'react';
import { Clock, CheckCircle2, AlertCircle, PlayCircle, FileText, AlertOctagon, AlertTriangle } from 'lucide-react';

type ModalityType = 'CT' | 'MRI' | 'MR' | 'XRAY' | 'CR' | 'DR' | 'US' | 'NM' | 'MG' | 'PT' | string;

interface ModalityBadgeProps {
    modality: ModalityType;
    className?: string;
}

const getModalityColor = (modality: string) => {
    const normalized = modality?.toUpperCase() || '';

    if (normalized.includes('CT')) return 'bg-blue-100 text-blue-700 border-blue-200';
    if (normalized === 'MR' || normalized.includes('MRI')) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (['CR', 'DR', 'DX', 'XRAY', 'X-RAY'].some(m => normalized.includes(m))) return 'bg-amber-100 text-amber-700 border-amber-200';
    if (normalized.includes('US')) return 'bg-purple-100 text-purple-700 border-purple-200';
    if (normalized.includes('NM') || normalized.includes('PT') || normalized.includes('PET')) return 'bg-rose-100 text-rose-700 border-rose-200';
    if (normalized.includes('MG')) return 'bg-pink-100 text-pink-700 border-pink-200';

    return 'bg-gray-100 text-gray-700 border-gray-200';
};

export const ModalityBadge: React.FC<ModalityBadgeProps> = ({ modality, className = '' }) => {
    const colorClass = getModalityColor(modality);

    return (
        <span className={`inline-flex items-center justify-center px-2.5 py-0.5 rounded-lg text-[14px] font-bold border ${colorClass} ${className}`}>
            {modality || '—'}
        </span>
    );
};

interface StatusBadgeProps {
    status: string;
    className?: string;
}

const getStatusConfig = (status: string) => {
    const normalized = status?.toLowerCase().replace(/_/g, ' ') || '';
    const original = status || '';

    // Handle ON_HOLD specifically
    if (normalized === 'on hold' || original === 'ON_HOLD') {
        return {
            color: 'bg-amber-50 text-amber-700 border-amber-200',
            icon: FileText,
            label: 'ON HOLD'
        };
    }

    // Handle ASSIGNED status
    if (normalized === 'assigned') {
        return {
            color: 'bg-purple-50 text-purple-700 border-purple-200',
            icon: FileText,
            label: 'Assigned'
        };
    }

    // Handle NEW status - show as "New"
    if (normalized === 'new') {
        return {
            color: 'bg-sky-50 text-sky-700 border-sky-200',
            icon: Clock,
            label: 'New'
        };
    }

    if (normalized.includes('progress') || normalized === 'reading') {
        return {
            color: 'bg-blue-50 text-blue-700 border-blue-200',
            icon: PlayCircle,
            label: 'In Progress'
        };
    }

    if (normalized.includes('complete') || normalized === 'finalized' || normalized === 'signed') {
        return {
            color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
            icon: CheckCircle2,
            label: 'Completed'
        };
    }

    if (normalized.includes('urgent') || normalized.includes('stat')) {
        return {
            color: 'bg-red-50 text-red-700 border-red-200',
            icon: AlertCircle,
            label: 'STAT'
        };
    }

    // Handle Pending status explicitly
    if (normalized === 'pending' || normalized === 'scheduled') {
        return {
            color: 'bg-gray-100 text-gray-600 border-gray-200',
            icon: Clock,
            label: 'Pending'
        };
    }

    // Default fallback - format the status nicely
    return {
        color: 'bg-gray-50 text-gray-600 border-gray-200',
        icon: FileText,
        label: normalized.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || 'Unknown'
    };
};

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status, className = '' }) => {
    const config = getStatusConfig(status);

    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-medium border whitespace-nowrap ${config.color} ${className}`}>
            <span>{config.label}</span>
        </span>
    );
};

interface PriorityBadgeProps {
    priority: string;
    className?: string;
}

const getPriorityConfig = (priority: string) => {
    const normalized = priority?.toLowerCase() || '';

    if (normalized === 'stat') {
        return {
            color: 'bg-red-100 text-red-700 border-red-200',
            icon: AlertOctagon,
            label: 'STAT'
        };
    }

    if (normalized === 'critical') {
        return {
            color: 'bg-red-100 text-red-700 border-red-200',
            icon: AlertOctagon,
            label: 'Critical'
        };
    }

    if (normalized === 'urgent' || normalized === 'high') {
        return {
            color: 'bg-orange-100 text-orange-700 border-orange-200',
            icon: AlertTriangle,
            label: 'Urgent'
        };
    }

    if (normalized === 'routine' || normalized === 'low' || normalized === 'normal') {
        return {
            color: 'bg-gray-100 text-gray-600 border-gray-200',
            icon: Clock,
            label: 'Routine'
        };
    }

    return {
        color: 'bg-gray-50 text-gray-500 border-gray-200',
        icon: Clock,
        label: priority || 'Routine'
    };
};

export const PriorityBadge: React.FC<PriorityBadgeProps> = ({ priority, className = '' }) => {
    const config = getPriorityConfig(priority);

    return (
        <div className={`flex items-center gap-1.5 ${className}`}>
            <span className={`text-[14px] font-medium ${config.color.replace('bg-', 'text-').split(' ')[1]}`}>
                {config.label}
            </span>
        </div>
    );
};
